import React, { useState, useEffect } from 'react';
import { Storage } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';
import { useTheme, styled } from '@mui/material/styles';
import { Stack } from '@mui/joy';
import { useForm, Controller } from 'react-hook-form';
import { useAdmin } from 'src/hooks';
import {
  Typography,
  // FormControlLabel, // unused, only referenced by commented-out JSX below
  Select as MuiSelect,
  MenuItem,
  OutlinedInput,
  InputLabel,
  FormControl,
  Checkbox as MuiCheckbox,
} from '@mui/material';
import { Modal, TextField, Button, Checkbox, IconButton, Table } from 'src/components';
import { ConfigurationCard } from '../ConfigurationCard';
// DemographicsConfig not on the card spec, keeping the page around but unused for now
import { ConsentFormConfig /* , DemographicsConfig */ } from './configPages';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
// PersonSearchRoundedIcon, SickRoundedIcon, MedicationRoundedIcon only referenced by commented-out JSX below
// import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
// import SickRoundedIcon from '@mui/icons-material/SickRounded';
// import MedicationRoundedIcon from '@mui/icons-material/MedicationRounded';

const Container = styled(Stack)({
  margin: '15px 10px',
  gap: 20,
});
const Row = styled(Stack)({
  flexDirection: 'row',
  gap: 10,
});
const Separated = styled(Stack)({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
});
const ConfigRow = styled(Stack)({
  flexDirection: 'row',
  display: 'flex',
  width: '100%',
  gap: 10,
  justifyContent: 'stretch',
});

const DEFAULT_GRANTED_ROLE = 'clinician';

const ListField = ({ title, header, rows, setRows }) => {
  const [editIndex, setEditIndex] = useState(-1);

  const addRow = () => {
    setRows([...rows, { value: '' }]);
    setEditIndex(rows.length);
  };

  return (
    <Stack sx={{ width: '100%' }}>
      <Separated>
        <Typography variant="h5">{title}</Typography>
        <IconButton icon="add" tooltip={`Add ${header}`} onClick={addRow} />
      </Separated>
      <Table
        columns={[
          { Header: header, accessor: 'value', isEditable: true, editType: 'text' },
          { Header: '', accessor: 'edit', width: 10 },
          { Header: '', accessor: 'delete', width: 10 },
        ]}
        data={rows}
        setData={setRows}
        editIndex={editIndex}
        setEditIndex={setEditIndex}
        rowsEditable
        rowsDeletable
      />
    </Stack>
  );
};

export const GroupModal = (props) => {
  const theme = useTheme();
  const { open, setOpen, selectedGroup } = props;
  const { groups, users, selected, createGroup, editUser } = useAdmin();
  const isNew = Object.keys(selected).length ? false : true;

  const defaultValues = isNew
    ? {
        groups: [],
      }
    : { ...selected };
  const form = useForm({
    defaultValues: isNew ? {} : selectedGroup,
  });
  const { register, handleSubmit, getValues, control, setValue, reset } = form;

  const [userGroups, setUserGroups] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [needsConsent, setNeedsConsent] = useState(false);
  const [file, setFile] = useState(null);
  const [page, setPage] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toListRows = (list) => (list || []).map((value) => ({ value }));
  const [medications, setMedications] = useState(toListRows(defaultValues.medications));
  const [surveys, setSurveys] = useState(toListRows(defaultValues.surveys));
  const [education, setEducation] = useState(toListRows(defaultValues.education));

  useEffect(() => {
    reset(defaultValues);
    setNeedsConsent(defaultValues.needsConsent ? true : false);
    setErrorMessage('');
    setFile(null);
    setMedications(toListRows(defaultValues.medications));
    setSurveys(toListRows(defaultValues.surveys));
    setEducation(toListRows(defaultValues.education));
  }, [open]);

  const addNewGroup = () => {
    const newData = [...userGroups];
    newData.push({
      group: '',
      roles: [],
    });
    setUserGroups(newData);
  };

  // Returns the S3 key (the schema's ConsentFormConfig.ref) for the uploaded PDF.
  const uploadConsentFormPDF = async () => {
    const result = await Storage.put(`/consentForm/${getValues().name}`, file);
    return result.key;
  };

  const onSubmit = async () => {
    const values = getValues();

    if (values.needsConsent && !file) {
      setErrorMessage(
        "Oops! You have checked 'needs consent', but haven't finished setting up your Consent Form configs."
      );
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const groupId = uuidv4();
      const userIds = values.userIds || [];

      const input = {
        id: groupId,
        app: 'RemoteMonitoring',
        name: values.name,
        institution: values.institution,
        shortDescription: values.shortDescription,
        fullDescription: values.fullDescription,
        needsConsent: !!values.needsConsent,
        isPublic: !!values.public,
        medications: medications.map((row) => row.value).filter(Boolean),
        surveys: surveys.map((row) => row.value).filter(Boolean),
        education: education.map((row) => row.value).filter(Boolean),
        users: userIds,
      };

      if (values.needsConsent) {
        const ref = await uploadConsentFormPDF();
        input.consentForm = {
          coordinates: { x: Number(values.x), y: Number(values.y) },
          includeDate: !!values.includeDate,
          page: parseInt(values.signaturePage, 10),
          ref,
          ...(values.includeDate
            ? { dateCoordinates: { x: Number(values.dateX), y: Number(values.dateY) } }
            : {}),
        };
      }

      await createGroup(input);

      // Grant access: append this group to each selected user's existing roles
      // (roles is a full-replace field on update, so we merge rather than overwrite).
      await Promise.all(
        userIds.map((userId) => {
          const user = users.find((u) => u.id === userId);
          if (!user) return null;
          const newRoles = [
            // Strip query-only fields (e.g. __typename) - mutation inputs only accept role/groupID.
            ...(user.roles || []).map(({ role, groupID }) => ({ role, groupID })),
            { role: DEFAULT_GRANTED_ROLE, groupID: groupId },
          ];
          return editUser({ id: user.id, roles: newRoles });
        })
      );

      setIsSubmitting(false);
      setOpen(false);
    } catch (error) {
      console.error('Error creating group:', error);
      setIsSubmitting(false);
      setErrorMessage('Oops! Something went wrong creating the group. Please try again.');
    }
  };

  return (
    <Modal
      label="create-group-modal"
      closeButton
      title={`${isNew ? 'Create' : 'Edit'} Group`}
      {...props}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Container direction="column" sx={{ display: page === 0 ? 'flex' : 'none' }}>
          <Separated>
            <Typography variant="h4">Group Info</Typography>
            <Stack direction="row">
              <Controller
                name="public"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="is-public-input"
                    defaultChecked={field.value}
                    label="public"
                    {...field}
                  />
                )}
              />
              <Controller
                name="needsConsent"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="needs-consent-input"
                    label="needs consent"
                    {...field}
                    defaultChecked={field.value}
                    onChange={(val) => {
                      setNeedsConsent(val.target.checked);
                      setValue('needsConsent', val.target.checked);
                    }}
                  />
                )}
              />
            </Stack>
          </Separated>
          <Row>
            <Controller
              name="name"
              control={control}
              rules={{ required: true, maxLength: 20 }}
              render={({ field }) => (
                <TextField
                  id="name-input"
                  label="Name"
                  size="small"
                  required
                  InputProps={{
                    readOnly: !isNew,
                  }}
                  {...field}
                />
              )}
            />
            <Controller
              name="institution"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  id="institution-input"
                  label="Institution"
                  size="small"
                  required
                  {...field}
                />
              )}
            />
            <Controller
              name="shortDescription"
              control={control}
              rules={{ required: true, maxLength: 20 }}
              render={({ field }) => (
                <TextField
                  id="short-description-input"
                  label="Short Description"
                  size="small"
                  required
                  sx={{ minWidth: '200px' }}
                  {...field}
                />
              )}
            />
          </Row>
          <Row>
            <Controller
              name="fullDescription"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  id="full-description-input"
                  label="Full Description"
                  sx={{
                    flexGrow: 3,
                    height: '100px !important',
                    '& div': {
                      height: '100px !important',
                    },
                    '& textarea': {
                      height: '80px !important',
                    },
                  }}
                  size="small"
                  required
                  multiline
                  {...field}
                />
              )}
            />
          </Row>
          <Row>
            <Controller
              name="userIds"
              control={control}
              render={({ field }) => (
                <FormControl size="small" sx={{ minWidth: '250px' }}>
                  <InputLabel id="users-select-label">Users</InputLabel>
                  <MuiSelect
                    labelId="users-select-label"
                    id="users-select"
                    size="small"
                    multiple
                    value={field.value || []}
                    onChange={(e) => field.onChange(e.target.value)}
                    input={<OutlinedInput label="Users" />}
                    renderValue={(selected) =>
                      selected
                        .map((id) => users.find((user) => user.id === id)?.email)
                        .filter(Boolean)
                        .join(', ')
                    }>
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        <MuiCheckbox checked={(field.value || []).indexOf(user.id) > -1} />
                        {user.email}
                      </MenuItem>
                    ))}
                  </MuiSelect>
                </FormControl>
              )}
            />
          </Row>
          <Row>
            <ListField
              title="Medications"
              header="Medication"
              rows={medications}
              setRows={setMedications}
            />
            <ListField title="Surveys" header="Survey" rows={surveys} setRows={setSurveys} />
            <ListField
              title="Education"
              header="Education Item"
              rows={education}
              setRows={setEducation}
            />
          </Row>
          <Separated sx={{ marginTop: '10px' }}>
            <Typography variant="h4" sx={{ lineHeight: '40px' }}>
              Configurations
            </Typography>
          </Separated>
          <ConfigRow>
            {needsConsent ? (
              <ConfigurationCard
                id="consent-form-config-card"
                title="Consent Form"
                icon={<DescriptionRoundedIcon />}
                onClick={() => setPage(1)}
              />
            ) : null}
            {/* Not on the card spec, commented out for now
            <ConfigurationCard
              id="demographics-config-card"
              title="Demographics"
              icon={<PersonSearchRoundedIcon />}
              onClick={() => setPage(2)}
            /> */}
            {/* Not yet implemented, commented out for now
            <ConfigurationCard
              id="symptoms-config-card"
              title="Symptoms"
              icon={<SickRoundedIcon />}
            />
            <ConfigurationCard
              id="medications-config-card"
              title="Medications"
              icon={<MedicationRoundedIcon />}
            /> */}
          </ConfigRow>
          <Separated>
            <Typography
              variant="paragraph"
              color={theme.palette.error.main}
              sx={{ wordWrap: 'break-word', maxWidth: 300 }}>
              {errorMessage}
            </Typography>
            <Button
              type="submit"
              disabled={isSubmitting}
              sx={{
                height: '40px !important',
                width: '200px',
                fontFamily: 'Comfortaa, Now, Arial',
              }}>
              {isSubmitting ? 'Creating...' : 'Create Group'}
            </Button>
          </Separated>
        </Container>
        <ConsentFormConfig
          isNew={isNew}
          form={form}
          file={file}
          setFile={setFile}
          setPage={setPage}
          sx={{ display: page === 1 ? 'flex' : 'none' }}
        />
        {/* Not on the card spec, commented out for now
        <DemographicsConfig
          isNew={isNew}
          form={form}
          setPage={setPage}
          sx={{ display: page === 2 ? 'flex' : 'none' }}
        /> */}
      </form>
    </Modal>
  );
};
